import liveObjectsService from "@/plugins/liveObjects";

const REALTIME_INITIAL_SYNC_WINDOW_MS = 5000;

function isInInitialSyncWindow (state) {
  if (!state.connectedAt) return false;
  return (Date.now() - state.connectedAt) < REALTIME_INITIAL_SYNC_WINDOW_MS;
}

const state = () => ({
  unsubscribes: [],
  isConnected: false,
  connectedAt: null,
  isDisabled: false,
  lastError: null,
  hasConnected: false,
  disconnectedAt: null,
});

const mutations = {
  setConnected (state, value) {
    state.isConnected = value;
    state.connectedAt = value ? Date.now() : null;
    if (value) state.hasConnected = true;
  },
  setDisconnectedAt (state, ts) {
    state.disconnectedAt = ts;
  },
  setDisabled (state, value) {
    state.isDisabled = value;
  },
  setLastError (state, error) {
    state.lastError = error;
  },
  addUnsubscribe (state, fn) {
    if (typeof fn === "function") {
      state.unsubscribes.push(fn);
    }
  },
  clearUnsubscribes (state) {
    state.unsubscribes.forEach(unsub => {
      try { unsub(); } catch (error) { console.warn(error); }
    });
    state.unsubscribes = [];
  }
};

// Module-scoped guard (not Vuex state) — liveObjectsService is a process-wide
// singleton, so this handler only needs to be attached once for the life of
// the app, regardless of how many times `init` runs.
let connectionStateHandlerAttached = false;

function attachConnectionStateHandler ({ state, commit, dispatch }) {
  if (connectionStateHandlerAttached) {
    return;
  }
  connectionStateHandlerAttached = true;

  liveObjectsService.onConnectionStateChange((stateChange) => {
    const current = stateChange.current;

    if (current === 'connected') {
      const wasDisconnected = !state.isConnected;
      commit('setConnected', true);
      // Ably's own connection dropped and recovered (independent of any
      // browser online/offline event, e.g. a brief service blip) — catch up
      // on anything that may have been missed while it was down.
      if (wasDisconnected && state.hasConnected) {
        dispatch('orders/reconcileOrdersWithRealtime', null, { root: true }).catch((e) => console.warn(e));
      }
      return;
    }

    // 'disconnected' is transient — the SDK retries on its own and resumes the
    // session with message continuity. Flipping isConnected there would put
    // every Pusher event and the tables view into HTTP-fallback mode, whose
    // backend-shaped payloads fight the Ably state. Only a prolonged loss
    // ('suspended') or a dead client counts as disconnected.
    if (['suspended', 'failed', 'closed'].includes(current)) {
      commit('setConnected', false);
      commit('setDisconnectedAt', Date.now());
    }
  });
}

const actions = {
  async init (context) {
    const { state, commit, dispatch } = context;
    attachConnectionStateHandler(context);

    if (state.isDisabled || !liveObjectsService.isReady() || state.isConnected) {
      return;
    }
    const isReconnect = state.hasConnected;
    console.log('[Realtime] init called, isReconnect=', isReconnect);
    commit('clearUnsubscribes');

    try {
      const unsubUpdate = await liveObjectsService.subscribeToOrderUpdates((message) => {
        if (message?.synced) {
          dispatch('orders/applyOrdersSnapshot', null, { root: true }).catch(e => console.warn(e));
          return;
        }
        const payload = message?.data;
        if (!payload) return;

        if (payload.source === liveObjectsService.getClientId()) {
          return;
        }
        dispatch('orders/applyRealtimeOrder', { ...payload, __fromRealtime: true }, { root: true });
      });

      const unsubDelete = await liveObjectsService.subscribeToOrderDeletion((message) => {
        const payload = message?.data;
        if (!payload) return;
        if (payload.source === liveObjectsService.getClientId()) {
          return;
        }
        dispatch('orders/applyRealtimeDeletion', payload, { root: true });
      });

      const unsubTablesReplace = await liveObjectsService.subscribeToTablesReplace((message) => {
        const payload = message?.data;
        if (!payload || payload.source === liveObjectsService.getClientId()) return;
        if (isInInitialSyncWindow(state)) return;
        dispatch('config/applyRealtimeTablesReplace', { ...payload, __fromRealtime: true }, { root: true });
      });

      const unsubTableUpdate = await liveObjectsService.subscribeToTableUpdates((message) => {
        const payload = message?.data;
        if (!payload || payload.source === liveObjectsService.getClientId()) return;
        if (isInInitialSyncWindow(state)) return;
        dispatch('config/applyRealtimeTable', { ...payload, __fromRealtime: true }, { root: true });
      });

      const unsubSections = await liveObjectsService.subscribeToSectionsUpdate((message) => {
        const payload = message?.data;
        if (!payload || payload.source === liveObjectsService.getClientId()) return;
        if (isInInitialSyncWindow(state)) return;
        dispatch('config/applyRealtimeSectionsUpdate', { ...payload, __fromRealtime: true }, { root: true });
      });

      const unsubShifts = await liveObjectsService.subscribeToShiftUpdate((message) => {
        const payload = message?.data;
        if (!payload || payload.source === liveObjectsService.getClientId()) return;
        dispatch('shifts/applyRealtimeShift', { ...payload, __fromRealtime: true }, { root: true });
      });

      const unsubProduct86dSet = await liveObjectsService.subscribeToProduct86dSet((message) => {
        const payload = message?.data;
        if (!payload || payload.source === liveObjectsService.getClientId()) return;
        dispatch('products86d/applyRealtime', payload, { root: true });
      });

      const unsubProduct86dRemove = await liveObjectsService.subscribeToProduct86dRemove((message) => {
        const payload = message?.data;
        if (!payload) return;
        if (payload.source === liveObjectsService.getClientId()) return;
        dispatch('products86d/applyRealtime', payload, { root: true });
      });

      // Backend-originated auto-86 changes (e.g. stock set to 0 from dashboard).
      // Uses applyRealtimeIfNewer to ignore subscription-continuity duplicates that
      // arrive after reconcileFromHistory has already applied the authoritative state.
      const unsubBackendProduct86d = await liveObjectsService.subscribeToBackendProduct86dUpdate((message) => {
        const payload = message?.data;
        if (!payload) return;
        dispatch('products86d/applyRealtimeIfNewer', { payload, timestamp: message.timestamp }, { root: true });
        liveObjectsService.publishProduct86d(payload).catch(() => {});
      });

      commit('addUnsubscribe', unsubUpdate);
      commit('addUnsubscribe', unsubDelete);
      commit('addUnsubscribe', unsubTablesReplace);
      commit('addUnsubscribe', unsubTableUpdate);
      commit('addUnsubscribe', unsubSections);
      commit('addUnsubscribe', unsubShifts);
      commit('addUnsubscribe', unsubProduct86dSet);
      commit('addUnsubscribe', unsubProduct86dRemove);
      commit('addUnsubscribe', unsubBackendProduct86d);
      commit('setConnected', true);
      commit('setLastError', null);

      dispatch('orders/fetchOpenOrders', null, { root: true }).catch(e => console.warn(e));
      console.log('[Realtime] dispatching reconcileFromSnapshot, isReconnect=', isReconnect);
      dispatch('products86d/reconcileFromSnapshot', null, { root: true })
        .catch(e => console.warn(e))
        .then(() => {
          if (isReconnect) {
            console.log('[Realtime] dispatching reconcileFromHistory after snapshot');
            dispatch('products86d/reconcileFromHistory', null, { root: true }).catch(e => console.warn(e));
          }
        });
    } catch (error) {
      console.error('[Realtime] Failed to initialize subscriptions', error);
      commit('setLastError', error?.message || 'Realtime initialization failed');
      dispatch('disable');
    }
  },
  teardown ({ commit }) {
    commit('clearUnsubscribes');
    commit('setConnected', false);
    commit('setDisconnectedAt', Date.now());
  },
  disable ({ commit }) {
    commit('clearUnsubscribes');
    commit('setConnected', false);
    commit('setDisabled', true);
  },
  enable ({ commit }) {
    commit('setDisabled', false);
  },
  handleConnectionChange ({ state, dispatch }, isOnline) {
    if (!isOnline) {
      dispatch('teardown');
      return;
    }
    if (!state.isDisabled) {
      dispatch('init');
    }
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};
